import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ActionModal } from "@/app/ActionModal";
import { errorMessage } from "@/app/api";
import { useCanWrite } from "@/app/auth";
import { EXPORT_STATUS, EXPORTS_SHOWN, exportOpen, filterSummary, formatSize, messageExports, rangeLabel, useExportNames, useMessageExports, type MessageExport, type NameOf } from "@/app/exports";
import { Alert, Button, EmptyState, formatTime, KeyValue, Pill, ShareBar, Spinner } from "@/ui";

/** 任务中心里的消息导出（决策 D202、D205）：每个任务一块，写明范围与条件、状态、进度或结果，完成后下载；能写的账号可以删除（进行中的随之停下）。 */
export function ExportTasks() {
  const list = useMessageExports();
  const total = useQuery({ queryKey: ["message-exports", "count"], queryFn: messageExports.count });
  const names = useExportNames();
  const [removing, setRemoving] = useState<MessageExport | null>(null);
  const rows = list.data ?? [];
  const more = (total.data?.count ?? 0) > rows.length;

  return (
    <>
      {list.error && <Alert tone="danger" title="加载失败">{errorMessage(list.error)}</Alert>}
      {list.isPending && <Spinner label="加载导出任务" />}
      {!list.isPending && !rows.length && <EmptyState title="没有导出任务">在消息记录页点“导出 Excel”，按当前条件建一个。</EmptyState>}
      {rows.map((e) => <ExportCard key={e.id} e={e} names={names} onRemove={() => setRemoving(e)} />)}
      {more && <span className="ax-muted">只列出最近 {EXPORTS_SHOWN} 个任务，共 {total.data?.count} 个；任务保留 7 天。</span>}
      {removing && (
        <ActionModal title={`删除导出 #${removing.id}`} confirmLabel="删除" danger action={() => messageExports.remove(removing.id)} invalidate={["message-exports"]}
          message={`已删除导出 #${removing.id}`} onClose={() => setRemoving(null)}
          note={<>{exportOpen(removing) ? "任务还没完成，删除后随即停止。" : "导出的文件一起删除，不能再下载。"}此操作写入审计日志。</>} />
      )}
    </>
  );
}

/** 一个任务：标题行是编号、状态与操作，下面是范围、条件与进度或结果。 */
function ExportCard({ e, names, onRemove }: { e: MessageExport; names: NameOf; onRemove: () => void }) {
  const canWrite = useCanWrite();
  const conditions = filterSummary(e.filter, names);
  const status = EXPORT_STATUS[e.status];
  return (
    <section className="ax-summary" aria-label={`导出 #${e.id}`}>
      <div className="ax-row">
        <span className="ax-mono">#{e.id}</span>
        <Pill tone={status.tone}>{status.label}</Pill>
        <span className="ax-row" style={{ marginLeft: "auto" }}>
          {e.status === "completed" && <Button size="sm" variant="primary" onClick={() => window.location.assign(messageExports.fileURL(e.id))}>下载</Button>}
          {canWrite && <Button size="sm" variant="ghost" className="is-danger" onClick={onRemove}>删除</Button>}
        </span>
      </div>
      <KeyValue items={[
        { label: "时间范围", value: rangeLabel(e.from, e.to) },
        { label: "筛选条件", value: conditions.length ? conditions.join("；") : "无" },
        { label: "字段", value: `${e.fields.length} 个` },
        { label: "创建", value: `${e.operator} · ${formatTime(e.created_at)}` },
      ]} />
      <ExportProgress e={e} />
    </section>
  );
}

/** 进度或结果：进行中画比例条与已导出条数，完成写条数与大小（截断时提示），失败写原因。 */
function ExportProgress({ e }: { e: MessageExport }) {
  const percent = Math.floor(e.progress * 100);
  switch (e.status) {
    case "queued":
      return <span className="ax-muted">排队中，前面的任务做完就开始。</span>;
    case "running":
      return (
        <div className="ax-section">
          <ShareBar label={`导出进度 ${percent}%`} segments={[{ key: "done", start: 0, end: e.progress, color: "var(--brand)", label: `已导出 ${e.rows.toLocaleString()} 条，约 ${percent}%` }]} />
          <span className="ax-muted">已导出 {e.rows.toLocaleString()} 条，约 {percent}%</span>
        </div>
      );
    case "completed":
      return (
        <>
          <span className="ax-muted">共 {e.rows.toLocaleString()} 条，文件约 {formatSize(e.file_size)}{e.finished_at && `，${formatTime(e.finished_at)} 完成`}</span>
          {e.truncated && <Alert tone="warning" title="只导出了一部分">达到 100 万条的上限，只导出了范围内最新的 100 万条；缩小时间范围分次导出。</Alert>}
        </>
      );
    default:
      return <Alert tone="danger" title="导出失败">{e.error ?? "未知原因"}，可以重新建一个任务。</Alert>;
  }
}
